/**
 * Takes patient data out of what the app sends to Sentry: events,
 * breadcrumbs, spans and replay recordings.
 *
 * URLs keep their path (ids only) and lose any query value that could be a
 * name or phone (`?search=Mona`); messages lose e-mail addresses and phone
 * numbers; objects lose the values of keys that name personal or clinical
 * data. The API runs the same rules on its side (dentacares-api
 * src/common/logging/pii-scrub.ts); keep the two in step.
 */

export const REDACTED = '[redacted]';

// Sentry's event shapes are walked generically here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dict = Record<string, any>;

const SENSITIVE_KEY =
  /^(name|first_?name|middle_?name|last_?name|full_?name|display_?name|patient_?name|guardian_?name|username|gender|dob|date_?of_?birth|birth_?date|birthday|national_?id|phone|phone_?number|phones|mobile|whatsapp|tel|email|emails|address|street|ip_?address|notes?|comments?|description|reason|diagnosis|allerg(y|ies)|medications?|medical_?history|conditions?|complaint|symptoms|answers?|title|subject|body|content|text|html|caption|original_?name|file_?name|password|token|otp|secret|authorization|cookies?|signature|data_?url|arguments)$/i;

const SAFE_QUERY_KEY =
  /^(page|page_?size|per_?page|limit|offset|cursor|before|after|sort|sort_?by|sort_?order|order|order_?by|dir|status(es)?|types?|kinds?|category|scope|view|mode|tab|lang|locale|currency|from|to|start|end|start_?date|end_?date|date|date_?from|date_?to|month|year|period|range|granularity|interval|group_?by|as_?of|tz|timezone|include|fields|active|archived|include_?archived|ids?|[a-z_]*_?ids?|t|v)$/i;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HARMLESS_QUERY_VALUE =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{4}-\d{2}(-\d{2}([T ][\d:.]+Z?)?)?|true|false|\d{1,4})$/i;
const EMAIL =
  /[^\s@<>"'(),;:=/\\]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?![\w-])/gi;
const PHONE = /(?<![\w-])\+?[0-9٠-٩۰-۹]{9,15}(?![\w-])/g;
const QUERY_IN_TEXT = /\?([\w.%[\]-]+=[^\s"'#<>]*)/g;

function scrubQueryString(query: string): string {
  return query
    .split('&')
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return pair;
      const key = pair.slice(0, eq);
      let value = pair.slice(eq + 1);
      try {
        value = decodeURIComponent(value.replace(/\+/g, ' '));
      } catch {
        // keep the raw value for the checks below
      }
      const keep =
        value === '' ||
        HARMLESS_QUERY_VALUE.test(value) ||
        (SAFE_QUERY_KEY.test(key.replace(/\[.*$/, '')) &&
          scrubText(value) === value);
      return keep ? pair : `${key}=${REDACTED}`;
    })
    .join('&');
}

/** Free text with e-mail addresses, phone numbers and query values replaced. */
export function scrubText(text: string): string {
  if (typeof text !== 'string' || text === '') return text;
  return text
    .replace(
      QUERY_IN_TEXT,
      (_m, query: string) => `?${scrubQueryString(query)}`,
    )
    .replace(EMAIL, '[email]')
    .replace(PHONE, '[phone]');
}

/** A URL or path with personal values taken out of its query string. */
export function scrubUrl(url: string): string {
  if (typeof url !== 'string') return url;
  const q = url.indexOf('?');
  if (q === -1) return scrubText(url);
  const hash = url.indexOf('#', q);
  return `${scrubText(url.slice(0, q))}?${scrubQueryString(
    url.slice(q + 1, hash === -1 ? undefined : hash),
  )}`;
}

/** Any value with personal data taken out. Never mutates. */
export function scrubValue(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return scrubText(value);
  if (typeof value !== 'object') return value;
  if (depth >= 8) return '[truncated]';
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1));
  const out: Dict = {};
  for (const [key, item] of Object.entries(value as Dict)) {
    if (item === null || item === undefined || item === '') out[key] = item;
    else if (/^(url|href|path|from|to)$/i.test(key) && typeof item === 'string')
      out[key] = scrubUrl(item);
    else if (SENSITIVE_KEY.test(key))
      out[key] =
        typeof item === 'boolean' ||
        typeof item === 'number' ||
        (typeof item === 'string' && UUID.test(item))
          ? item
          : REDACTED;
    else out[key] = scrubValue(item, depth + 1);
  }
  return out;
}

export function scrubBreadcrumb<T extends Dict>(crumb: T): T {
  const out: Dict = { ...crumb };
  if (typeof out.message === 'string') out.message = scrubText(out.message);
  // A console breadcrumb's arguments are whatever was printed.
  if (out.data) out.data = scrubValue(out.data);
  return out as T;
}

function scrubSpan(span: Dict): Dict {
  const out: Dict = { ...span };
  if (typeof out.description === 'string')
    out.description = scrubUrl(out.description);
  if (out.data && typeof out.data === 'object') {
    out.data = Object.fromEntries(
      Object.entries(out.data as Dict).map(([key, value]) => [
        key,
        typeof value !== 'string'
          ? value
          : /url|target|query|route|path/i.test(key)
            ? scrubUrl(value)
            : scrubText(value),
      ]),
    );
  }
  return out;
}

const SDK_CONTEXTS = new Set([
  'app',
  'browser',
  'culture',
  'device',
  'os',
  'profile',
  'replay',
  'runtime',
  'flags',
]);

/** A Sentry event (error or transaction) with patient data taken out. */
export function scrubEvent<T extends Dict>(event: T): T {
  const out: Dict = { ...event };
  if (out.request) {
    const request: Dict = { ...out.request };
    delete request.data;
    delete request.cookies;
    delete request.query_string;
    if (typeof request.url === 'string') request.url = scrubUrl(request.url);
    if (request.headers)
      request.headers = { 'User-Agent': request.headers['User-Agent'] };
    out.request = request;
  }
  out.user = out.user?.id ? { id: out.user.id } : undefined;
  if (typeof out.message === 'string') out.message = scrubText(out.message);
  if (typeof out.transaction === 'string')
    out.transaction = scrubUrl(out.transaction);
  if (out.exception?.values) {
    out.exception = {
      ...out.exception,
      values: out.exception.values.map((ex: Dict) => ({
        ...ex,
        value: typeof ex.value === 'string' ? scrubText(ex.value) : ex.value,
      })),
    };
  }
  if (out.tags)
    out.tags = Object.fromEntries(
      Object.entries(out.tags as Dict).map(([k, v]) => [
        k,
        typeof v === 'string' ? scrubUrl(v) : v,
      ]),
    );
  if (out.extra) out.extra = scrubValue(out.extra);
  if (out.contexts)
    out.contexts = Object.fromEntries(
      Object.entries(out.contexts as Dict).map(([k, v]) => [
        k,
        k === 'trace' && v && typeof v === 'object'
          ? scrubSpan(v)
          : SDK_CONTEXTS.has(k)
            ? v
            : scrubValue(v),
      ]),
    );
  if (Array.isArray(out.breadcrumbs))
    out.breadcrumbs = out.breadcrumbs.map(scrubBreadcrumb);
  if (Array.isArray(out.spans)) out.spans = out.spans.map(scrubSpan);
  return out as T;
}

/**
 * A replay recording event with URLs scrubbed: page loads, fetches and the
 * breadcrumbs replay keeps of its own. Screen text, inputs and media are
 * already masked by the replay integration's options.
 */
export function scrubRecordingEvent<T extends Dict>(event: T): T {
  const payload = event?.data?.payload;
  if (!payload || typeof payload !== 'object') return event;
  const clean: Dict = { ...payload };
  if (typeof clean.description === 'string')
    clean.description = scrubUrl(clean.description);
  if (typeof clean.message === 'string')
    clean.message = scrubText(clean.message);
  if (clean.data) clean.data = scrubValue(clean.data);
  return { ...event, data: { ...event.data, payload: clean } };
}
